import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { setAlert } from "../../features/slices/alertSlice";
import {
  useGetBankAccountsQuery,
  useLazyPlayerGetOtpQuery,
  useLazyPlayerVerifyOtpQuery,
} from "../../features/api/userApiSlice";
import * as Constant from "../../features/constant";

const PATH = process.env.PUBLIC_URL;
const EMPTY_CODE = ["", "", "", "", "", ""];
const RESEND_SECONDS = 120;

export const VERIFY_ACCOUNT_ITEM_KEYS = ["profile", "phone", "email", "id", "bank"];
const DEFAULT_VERIFY_ACCOUNT_ITEMS = ["phone", "email"];

// Master switch for the "User Verification" block (desktop sidebar + ProfileWallet2 mobile).
export const isVerifyAccountEnabled = () => process.env.REACT_APP_SHOW_VERIFY_ACCOUNT === "true";

// Standalone "ID Verification" row in ProfileWallet2. Off unless explicitly enabled.
export const isIdVerificationEnabled = () => process.env.REACT_APP_SHOW_ID_VERIFICATION === "true";

// REACT_APP_VERIFY_ACCOUNT_ITEMS="profile,phone,email,id,bank" picks the icons and their order.
// Unset or empty falls back to phone + email, which is what the sidebar always showed.
export const getVerifyAccountItems = () => {
  const items = String(process.env.REACT_APP_VERIFY_ACCOUNT_ITEMS || "")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter((item, index, all) => VERIFY_ACCOUNT_ITEM_KEYS.includes(item) && all.indexOf(item) === index);
  return items.length > 0 ? items : DEFAULT_VERIFY_ACCOUNT_ITEMS;
};

// Original image assets used by the desktop sidebar ("profile" has none; callers supply their own icon).
export const VERIFY_ICON_IMAGES = {
  phone: { verified: "mobile_verified.png", pending: "mobile_pre_verified.png" },
  email: { verified: "email_verified.png", pending: "email_pre_verified.png" },
  id: { verified: "id_verified.png", pending: "id_pre_verified.png" },
  bank: { verified: "bank_verified.png", pending: "bank_pre_verified.png" },
};
export const verifyIconSrc = (key, verified) =>
  VERIFY_ICON_IMAGES[key]
    ? `${PATH}/assets/img/wallet/verified_icon/${VERIFY_ICON_IMAGES[key][verified ? "verified" : "pending"]}`
    : null;

const OtpDialog = ({
  open,
  onClose,
  title,
  sentTo,
  maskedTarget,
  code,
  onChangeDigit,
  onKeyDownDigit,
  inputRefs,
  onVerify,
  seconds,
  onResend,
}) => {
  const textColor = process.env.REACT_APP_COLOR_TEXT || "white";
  return (
    <Dialog open={open} onClose={onClose} BackdropProps={{ invisible: true }}>
      <DialogTitle sx={{ padding: 0 }}>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <Typography sx={{ textAlign: "center", fontWeight: "bold", fontSize: "20px", color: textColor }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: "center", fontSize: "12px", color: textColor }}>{sentTo}</Typography>
        <Typography sx={{ textAlign: "center", fontWeight: "bold", fontSize: "16px", color: textColor, paddingTop: "5px" }}>
          {maskedTarget}
        </Typography>
        <br />
        <Grid container spacing={1} alignItems="center" justifyContent="center">
          {code.map((digit, index) => (
            <Grid item key={index}>
              <TextField
                variant="outlined"
                size="small"
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => onChangeDigit(index, e.target.value)}
                onKeyDown={(e) => onKeyDownDigit(index, e)}
                inputProps={{
                  maxLength: 1,
                  inputMode: "numeric",
                  style: { textAlign: "center", width: "10px", height: "15px" },
                }}
              />
            </Grid>
          ))}
        </Grid>
        <br />
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button variant="contained" color="error" onClick={onVerify}>
            VERIFY
          </Button>
        </DialogActions>
        <br />
        <Typography sx={{ textAlign: "center", fontSize: "12px", color: textColor }}>
          Experiencing any issues?{" "}
          <Link href="/contactus" style={{ color: "red", textDecoration: "none" }}>
            Contact Customer Support
          </Link>
        </Typography>
        <br />
        <Typography sx={{ textAlign: "center", fontSize: "12px", color: textColor }}>
          Didn't receive the PIN number?{" "}
          <Typography
            component="span"
            sx={{ color: "green", fontSize: "12px", cursor: seconds === 0 ? "pointer" : "not-allowed" }}
            onClick={seconds === 0 ? onResend : undefined}
          >
            Resend code ({seconds}s)
          </Typography>
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Shared "User Verification" behaviour: which icons to show, whether each is verified,
 * and what tapping it does. Phone/email open the OTP dialogs directly (same flow the
 * ProfileWallet sidebar always had); id/profile/bank navigate to their screens.
 *
 * Render `dialogs` once somewhere in the page.
 *
 * @param {object} params
 * @param {object} params.user        profile result (useGetProfileQuery().data.result)
 * @param {boolean} params.mobile     true → navigate to the standalone mobile routes
 * @param {string} [params.shareLink] passed to /myaccount on mobile, as the old mobile list did
 */
const useAccountVerification = ({ user, mobile, shareLink }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const itemKeys = getVerifyAccountItems();

  const [channel, setChannel] = useState(null); // "MOBILE" | "EMAIL" | null
  const [code, setCode] = useState(EMPTY_CODE);
  const [seconds, setSeconds] = useState(null);
  const [idInProgressOpen, setIdInProgressOpen] = useState(false);
  const inputRefs = useRef([]);

  const [getOtp] = useLazyPlayerGetOtpQuery();
  const [verifyOtp] = useLazyPlayerVerifyOtpQuery();
  const { data: bankAccounts } = useGetBankAccountsQuery(undefined, {
    skip: !isVerifyAccountEnabled() || !itemKeys.includes("bank"),
  });

  useEffect(() => {
    if (!(seconds > 0)) return;
    const timer = setTimeout(() => setSeconds((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  const openOtp = (otpChannel) => {
    setChannel(otpChannel);
    setCode(EMPTY_CODE);
    getOtp(otpChannel);
    setSeconds(RESEND_SECONDS);
  };

  const closeOtp = () => {
    setChannel(null);
    setCode(EMPTY_CODE);
  };

  const focusInput = (index) => inputRefs.current[index]?.focus();

  const handleChangeDigit = (index, value) => {
    const digit = value.length > 1 ? value.charAt(0) : value;
    setCode((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    focusInput(digit !== "" ? index + 1 : index - 1);
  };

  const handleKeyDownDigit = (index, event) => {
    if (event.key === "Backspace" && code[index] === "") {
      event.preventDefault();
      focusInput(index - 1);
    }
  };

  const handleVerify = async () => {
    const result = await verifyOtp({ channel, otp: code.join("") });
    if (result.isSuccess) {
      closeOtp();
      dispatch(setAlert({ isOpen: true, message: t("myaccountpage.verifyotpsuccess"), variant: "success" }));
      window.location.reload();
    } else {
      dispatch(setAlert({ isOpen: true, message: t("myaccountpage.verifyotpfailed"), variant: "error" }));
    }
  };

  const goScreen = (screen) =>
    navigate(Constant.getPath("/profileWallet"), { replace: true, state: { screen } });

  const handleIdClick = () => {
    if (user?.isIdentityVerified) return;
    if (user?.isIdVerificationSubmitted) {
      setIdInProgressOpen(true);
      return;
    }
    goScreen("id verification");
  };

  const definitions = {
    profile: {
      verified: true,
      onClick: () =>
        mobile
          ? navigate(Constant.getPath("/myaccount"), { state: { user, shareLink } })
          : goScreen("My Account"),
    },
    phone: {
      verified: !!user?.isPhoneVerified,
      onClick: () => !user?.isPhoneVerified && openOtp("MOBILE"),
    },
    email: {
      verified: !!user?.isEmailVerified,
      onClick: () => !user?.isEmailVerified && openOtp("EMAIL"),
    },
    id: {
      verified: !!user?.isIdentityVerified,
      onClick: handleIdClick,
    },
    bank: {
      verified: (bankAccounts?.result?.data?.length || 0) > 0,
      onClick: () => (mobile ? navigate(Constant.getPath("/bankaccount")) : goScreen("Bank Account")),
    },
  };

  const items = itemKeys.map((key) => ({ key, ...definitions[key] }));

  const mobileNumber = user?.mobileNumber || "";
  const email = user?.email || "";
  const textColor = process.env.REACT_APP_COLOR_TEXT || "white";

  const dialogs = (
    <>
      <OtpDialog
        open={channel === "MOBILE"}
        onClose={closeOtp}
        title="Verify Mobile Number"
        sentTo="A 6-digit PIN number has been sent to your phone."
        maskedTarget={`********${mobileNumber.substring(mobileNumber.length - 4, mobileNumber.length - 1)}`}
        code={code}
        onChangeDigit={handleChangeDigit}
        onKeyDownDigit={handleKeyDownDigit}
        inputRefs={inputRefs}
        onVerify={handleVerify}
        seconds={seconds}
        onResend={() => openOtp("MOBILE")}
      />
      <OtpDialog
        open={channel === "EMAIL"}
        onClose={closeOtp}
        title="Verify Email"
        sentTo="A 6-digit PIN number has been sent to your email."
        maskedTarget={`${email.substring(0, 2)}******@${email.split("@")[1] || ""}`}
        code={code}
        onChangeDigit={handleChangeDigit}
        onKeyDownDigit={handleKeyDownDigit}
        inputRefs={inputRefs}
        onVerify={handleVerify}
        seconds={seconds}
        onResend={() => openOtp("EMAIL")}
      />
      <Dialog open={idInProgressOpen} onClose={() => setIdInProgressOpen(false)} BackdropProps={{ invisible: true }}>
        <DialogTitle sx={{ padding: 0 }}>
          <DialogActions>
            <Button onClick={() => setIdInProgressOpen(false)}>X</Button>
          </DialogActions>
          <Typography sx={{ textAlign: "center", fontWeight: "bold", fontSize: "20px", color: textColor }}>
            In-Progress
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: "center", fontSize: "12px", color: textColor }}>
            You have submitted your ID Verification. Please wait for approval.
          </Typography>
          <br />
          <Typography sx={{ textAlign: "center", fontSize: "12px", color: textColor }}>
            Experiencing any issues?{" "}
            <Link href="/contactus" style={{ color: "red", textDecoration: "none" }}>
              Contact Customer Support
            </Link>
          </Typography>
          <br />
        </DialogContent>
      </Dialog>
    </>
  );

  return { items, actions: definitions, dialogs };
};

export default useAccountVerification;
